import React, { useState, useEffect } from 'react';
import { Nav } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import {
  Speedometer2,
  Key,
  HddNetwork,
  Bookmark,
  ListCheck,
  Gear,
  Hash,
  Clock,
  Bell,
  Shield
} from 'react-bootstrap-icons';
import { getSystemStatus } from '../../services/system';

const Sidebar = () => {
  const location = useLocation();
  const [systemStatus, setSystemStatus] = useState('online');
  const [lastUpdate, setLastUpdate] = useState('just now');

  const mainNavItems = [
    { name: 'Dashboard', href: '/', icon: Speedometer2 },
    { name: 'Tokens', href: '/tokens', icon: Key },
    { name: 'Proxies', href: '/proxies', icon: HddNetwork },
    { name: 'Channels', href: '/channels', icon: Hash },
    { name: 'Queue', href: '/queue', icon: Clock },
    { name: 'Rules', href: '/rules', icon: Bookmark },
    { name: 'Logs', href: '/logs', icon: ListCheck },
    { name: 'Notifications', href: '/notifications', icon: Bell },
    { name: 'Settings', href: '/settings', icon: Gear },
  ];

  const adminNavItem = { name: 'Admin Panel', href: '/admin', icon: Shield, adminOnly: true };

  // Get user data from localStorage for permissions
  const userData = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    // Fetch system status on component mount
    checkSystemStatus();
    
    // Set up periodic status checking
    const interval = setInterval(checkSystemStatus, 30000); // Every 30 seconds
    
    return () => clearInterval(interval);
  }, []);

  const checkSystemStatus = async () => {
    try {
      const status = await getSystemStatus();
      setSystemStatus(status.status);
      setLastUpdate(new Date().toLocaleTimeString());
    } catch (error) {
      console.error('Failed to fetch system status:', error);
      setSystemStatus('offline');
    }
  };

  const getStatusColor = () => {
    switch (systemStatus) {
      case 'online': return 'success';
      case 'degraded': return 'warning';
      case 'offline': return 'danger';
      default: return 'secondary';
    }
  };

  const getStatusText = () => {
    switch (systemStatus) {
      case 'online': return 'System Online';
      case 'degraded': return 'System Degraded';
      case 'offline': return 'System Offline';
      default: return 'Unknown Status';
    }
  };

  return (
    <div className="sidebar bg-white shadow-sm border-end" style={{ width: '250px', minHeight: '100vh' }}>
      <div className="p-3 border-bottom">
        <h5 className="fw-bold text-primary mb-0 d-flex align-items-center">
          <HddNetwork className="me-2" size={24} />
          Discord Reactor
        </h5>
      </div>
      
      <Nav className="flex-column p-3">
        {/* Main Navigation Items */}
        {mainNavItems.map((item, index) => {
          // Check permissions for admin-only pages
          if (item.href === '/settings' && !userData.is_admin) {
            return null;
          }
          
          return (
            <Nav.Item key={index} className="mb-1">
              <Nav.Link
                as={Link}
                to={item.href}
                className={`d-flex align-items-center rounded px-3 py-2 text-decoration-none transition-all ${
                  location.pathname === item.href 
                    ? 'bg-primary text-white shadow-sm' 
                    : 'text-dark hover-bg-light'
                }`}
                style={{ 
                  transition: 'all 0.2s ease-in-out',
                  border: 'none'
                }}
              >
                <item.icon className="me-3" size={18} />
                <span className="fw-medium">{item.name}</span>
              </Nav.Link>
            </Nav.Item>
          );
        })}

        {userData.is_admin && (
          <>
            <div className="border-top my-2"></div>
            
            {/* Admin Panel */}
            <Nav.Item className="mb-1">
              <Nav.Link
                as={Link}
                to={adminNavItem.href}
                className={`d-flex align-items-center rounded px-3 py-2 text-decoration-none transition-all ${
                  location.pathname === adminNavItem.href 
                    ? 'bg-warning text-dark shadow-sm' 
                    : 'text-dark hover-bg-light'
                }`}
                style={{ 
                  transition: 'all 0.2s ease-in-out',
                  border: 'none'
                }}
              >
                <adminNavItem.icon className="me-3" size={18} />
                <span className="fw-medium">{adminNavItem.name}</span>
              </Nav.Link>
            </Nav.Item>
          </>
        )}
      </Nav>
      
      {/* System Status */}
      <div className="mt-auto p-3 border-top">
        <div className="d-flex align-items-center mb-1">
          <div 
            className={`rounded-circle me-2 bg-${getStatusColor()}`} 
            style={{ width: '8px', height: '8px' }}
          ></div>
          <small className={`fw-medium text-${getStatusColor()}`}>
            {getStatusText()}
          </small>
        </div>
        <small className="text-muted">Last updated: {lastUpdate}</small>
      </div>
    </div>
  );
};

export default Sidebar;